<template>
	<div>
		<div class="chart-container sticky-with-body-scroll-left">
			<CCol md="4" class="chart-item">
				<p class="typo-h5 mb-3">
					All available stocks
				</p>
				<div class="chart-info-container">
					<div class="chart-label-container">
						<div class="chart-label-item">
							<p class="chart-label-title">
								Online stock
							</p>
							<p class="chart-label-value">
								{{ availableOnlineStock }}
							</p>
						</div>
						<div class="chart-label-item">
							<p class="chart-label-title">
								Offline stock
							</p>
							<p class="chart-label-value">
								{{ availableOfflineStock }}
							</p>
						</div>
					</div>
					<BaseChartPie
						v-if="!chart.isLoading"
						:data="chartData"
						:options="chartOptions"
					/>
				</div>
			</CCol>
			<p class="chart-title typo-subtitle mt-4">
				All SKU list
			</p>
		</div>
		<!-- Search Filter -->
		<BaseSearchFilter
			:active="isActive"
			:show="isActive"
			container-action-class="adjust-column"
		>
			<template #header>
				<CCol>
					<CInput
						v-model="query.keyword"
						:placeholder="$t('placeholder.stock_browser_sku')"
						@keyup.enter="onInputEnter"
					>
						<template #prepend-content>
							<FontAwesomeIcon :icon="['far', 'search']" />
						</template>
					</CInput>
				</CCol>
			</template>
			<template #action>
				<CDropdown
					:show.sync="adjustColumnButtonToggle"
					:color="adjustColumnButtonColor"
					toggler-text="Adjust column"
					class="container-adjust"
					block
				>
					<CContainer class="container-adjust-column p-0">
						<CRow class="m-0">
							<CCol class="typo-h5 pt-2 pl-3 mb-1" xl="12">
								Adjust channel columns
							</CCol>
							<CCol class="p-0" xl="12">
								<hr class="mt-2 mb-2">
							</CCol>
						</CRow>
						<CRow class="mt-2 ml-0 px-2">
							<CCol
								v-for="channel in transformChannels"
								:key="channel.id"
								xl="12"
								class="mb-3"
							>
								<CInputCheckbox
									:checked="channel.checked"
									:label="channel.name"
									custom
									class="list-checkbox"
									@update:checked="handleCheckedAdjustColumn($event, channel.id)"
								/>
							</CCol>
						</CRow>
					</CContainer>
				</CDropdown>
				<CButton
					block
					:disabled="loadingExportBtn"
					class="export-report-btn ml-3"
					@click="exportReport"
				>
					<CIcon
						v-if="!loadingExportBtn"
						name="cil-file"
						class="mr-2"
					/>
					<span
						v-else
						class="spinner-border spinner-border-sm mr-2"
						role="status"
						aria-hidden="true"
					>
					</span>
					{{ $t('button.export_report') }}
				</CButton>
			</template>
			<template #body>
				<CCol md="3">
					<BaseDropDown
						v-model="filter.brand"
						:options="brandOptions"
						:label-drop-down="$t('label.brand')"
						:placeholder="$t('message.selection')"
						:allow-empty="false"
						searchable
						label="name"
						track-by="value"
						@input="handleBrandChange"
					/>
				</CCol>
				<CCol md="9">
					<ProductsFilterCategory
						:category-id="query.category_id"
						@onCategoryChange="handleCategory"
					/>
				</CCol>
				<CCol md="3">
					<BaseDropDown
						v-model="filter.status"
						:options="SKU_STATUS_OPTIONS"
						:label-drop-down="$t('label.status')"
						:placeholder="$t('message.selection')"
						:searchable="false"
						:allow-empty="false"
						label="name"
						track-by="value"
						@input="handleStatusChange"
					/>
				</CCol>
				<CCol md="3">
					<BaseDropDown
						v-model="filter.type"
						:options="SKU_TYPE_OPTIONS"
						:label-drop-down="$t('label.product_type')"
						:placeholder="$t('message.selection')"
						:searchable="false"
						:allow-empty="false"
						label="name"
						track-by="value"
						@input="handleTypeChange"
					/>
				</CCol>
			</template>
			<template #footer>
				<CCol md="12">
					<CInputCheckbox
						:checked.sync="checkedStockAging"
						class="d-inline-block mb-3"
						label="Last purchase over 120 days"
						custom
						@update:checked="handleStockAging($event)"
					/>
				</CCol>
				<CCol md="8">
					{{ $t('message.sku_found', { number: totalCount }) }}
				</CCol>
				<CCol md="4">
					<button @click="onResetClick">
						{{ $t('button.resetFilters') }}
					</button>
				</CCol>
			</template>
		</BaseSearchFilter>

		<!-- Table, Pagination -->
		<BaseTable
			:is-loading="report.isLoading"
			:pagination="report.pagination"
			:fields="reportTableFields"
			:items="dataTable"
			:sort-columns="{
				sku_id: query.sort_by === 'sku_id' ? (query.sort_direction || 'asc') : 'asc',
				product_name: query.sort_by === 'product_name' ? (query.sort_direction || 'asc') : 'asc'
			}"
			:border-left="[
				'available-stock-cel',
				'reserved-cel',
				'status-cel',
				'channel-order-bnn-cel',
				'categories-cel',
			]"
			:border-right="[
				'committed-cel'
			]"
			hover
			fixed
			class="table-custom"
			@onSortClick="handleSortClick"
			@onPaginationClick="handlePageClick"
		>
			<template #categories="{ item = '' }">
				<p
					v-for="(value, index) in item"
					:key="index"
					class="m-2"
				>
					{{ value }}
				</p>
			</template>
			<template #status="{ item = {} }">
				<CBadge class="badge-sku-status" :color="item.color">
					{{ item.title }}
				</CBadge>
			</template>

			<template #latestCommitted="{ item = {} }">
				{{ item.date }}
				<span
					v-if="item.day >= 120"
					class="over-stock"
				>
					Over 120 days
				</span>
			</template>

			<template #no-items-view>
				<div class="empty-table-element">
					<p class="subtitle">
						{{ $t('message.notFoundSkuList') }}
					</p>
				</div>
			</template>
		</BaseTable>
	</div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import ProductsFilterCategory from '@/components/ProductsFilterCategory.vue';
import { randomSearchString, numberFormat, defaultValue } from '../helpers/utils';
import {
	SKU_STATUS_COLOR,
	SKU_STATUS_TITLE,
	SKU_STATUS_OPTIONS,
	SKU_TYPE_OPTIONS,
} from '../enums/sku';
import { getSelectedValue } from '../helpers/dropdown';
import { REPORT_TABLE_FIELDS } from '../enums/report';
import { CHANNEL_ORDER_QTY } from '../enums/stock';
import { updateUrl } from '../helpers/mixins';
import BaseChartPie from '../components/BaseChartPie.vue';
import { getExportReport } from '../services/api/report.api';


export default {
	name: 'Reports',
	components: {
		BaseChartPie,
		ProductsFilterCategory,
	},
	mixins: [updateUrl],
	data() {
		return {
			REPORT_TABLE_FIELDS,
			SKU_STATUS_OPTIONS,
			SKU_TYPE_OPTIONS,
			query: {
				keyword: this.$route.query.keyword || null,
				brand: this.$route.query.brand || null,
				status: this.$route.query.status || null,
				type: this.$route.query.type || null,
				sort_by: this.$route.query.sort_by || null,
				sort_direction: this.$route.query.sort_direction || null,
				stock_ageing: this.$route.query.stock_ageing || null,
				category_id: this.$route.query.category_id || null,
				page: this.$route.query.page || null,
				r: randomSearchString(),
			},
			filter: {
				brand: getSelectedValue(null, this.brandOptions),
				status: getSelectedValue(this.$route.query.status, SKU_STATUS_OPTIONS),
				type: getSelectedValue(this.$route.query.type, SKU_TYPE_OPTIONS),
			},
			chartOptions: {
				legend: {
					display: false,
				},
			},
			loadingExportBtn: false,
			adjustColumn: JSON.parse(localStorage.getItem('adjust_column')) || [], // channel ids
			adjustColumnButtonToggle: false,
			checkedStockAging: this.$route.query.stock_ageing === '1',
		};
	},
	computed: {
		...mapState('report', {
			report: 'report',
			chart: 'chart',
		}),
		...mapState('channelList', {
			channels: 'data',
			isLoadingChannels: 'isLoading',
		}),
		...mapGetters({
			brandOptions: 'brandList/brandOptions',
			categoryOptions: 'categoryList/categoryOptions',
		}),
		dataTable() {
			return this.report.data.map((item) => {
				const channels = this.transformFields.reduce((acc, cur) => {
					const value = item.channels || {};
					// convert to Kebab-case
					const keyValue = cur.key.replace('channel_', '').split(/[_\s]/).join('-').toLowerCase();
					acc[cur.key] = defaultValue(value[keyValue] || '0');
					return acc;
				}, {});
				const channelOrder = this.transformChannelOrderField.reduce((acc, cur) => {
					const value = item.channelOrderQty || {};
					const keyValue = cur.key.replace('channel_order_', '');
					acc[cur.key] = defaultValue(value[keyValue], '0');
					return acc;
				}, {});
				return {
					skuID: item.skuID,
					productName: item.productName,
					brand: item.brand,
					availableStock: item.availableStock,
					onlineStock: item.onlineStock,
					offlineStock: item.offlineStock,
					totalStock: item.totalStock,
					reserved: item.reserved,
					committed: item.committed,
					...channels,
					...channelOrder,
					categories: item.categories,
					status: {
						title: SKU_STATUS_TITLE[item.status],
						color: SKU_STATUS_COLOR[item.status],
					},
					type: item.type,
					eolStatus: item.eolStatus ? 'On' : 'Off',
					latestCommitted: {
						date: item.latestCommittedAt,
						day: item.latestCommittedInDays,
					},
				};
			});
		},
		channelOrder() {
			return this.report.data.reduce((acc, cur) => {
				return [...acc, ...Object.keys(cur.channelOrderQty)];
			}, []);
		},
		transformChannelOrderField() {
			if (this.channelOrder) {
				const channelOrderTotal = [...new Set(this.channelOrder)].reduce((acc, cur) => {
					return [...acc, {
						key: `channel_order_${cur}`,
						label: `Order Oty ${cur}`,
						class: `channel-order-${cur}`,
						cellClass: `channel-order-${cur}-cel`,
					}];
				}, []);
				if (channelOrderTotal.length) {
					const reoderChannelOrderTotal = channelOrderTotal.sort((a, b) => {
						if (b.key === CHANNEL_ORDER_QTY.TOTAL) {
							return -1;
						}
						return a.key.localeCompare(b.key);
					});
					return reoderChannelOrderTotal;
				}
			}
			return [];
		},
		transformFields() {
			return this.channels.reduce((acc, cur) => {
				if (this.adjustColumn.includes(cur.id)) {
					acc.push({
						key: `channel_${cur.name}`,
						label: cur.name,
						class: 'channel-col',
						cellClass: 'channel-cel',
					});
				}

				return acc;
			}, []);
		},
		reportTableFields() {
			return REPORT_TABLE_FIELDS.reduce((acc, cur) => {
				acc.push(cur);

				if (cur.key === 'committed') {
					acc.push(...this.transformFields);
					acc.push(...this.transformChannelOrderField);
				}

				return acc;
			}, []);
		},
		isActive() {
			return (
				this.filter.brand?.value !== null ||
				this.filter.status?.value !== null ||
				this.filter.type?.value !== null ||
				this.query.category_id !== null
			);
		},
		totalCount() {
			return numberFormat(this.report.pagination.total);
		},
		availableOnlineStock() {
			return numberFormat(this.chart.data.availableOnlineStock);
		},
		availableOfflineStock() {
			return numberFormat(this.chart.data.availableOfflineStock);
		},
		chartData() {
			return {
				borderWidth: 0,
				labels: ['Online stock', 'Offline stock'],
				datasets: [
					{
						backgroundColor: ['#ffcc00', '#63c2de'],
						borderWidth: 0,
						data: [this.chart.data.availableOnlineStock, this.chart.data.availableOfflineStock],
					},
				],
			};
		},
		adjustColumnButtonColor() {
			return this.adjustColumnButtonToggle ? 'secondary' : 'tertiary';
		},
		transformChannels() {
			return this.channels.map((channel) => {
				return {
					...channel,
					checked: this.adjustColumn.includes(channel.id),
				};
			});
		},
	},

	async created() {
		await this.fetchData();

		if (this.brandOptions.length === 1) {
			await this.getBrands();
		}

		if (this.categoryOptions.length === 1) {
			await this.getCategories();
		}

		const brand = this.$route.query.brand ? Number(this.$route.query.brand) : null;
		this.filter.brand = getSelectedValue(brand, this.brandOptions);
	},

	methods: {
		...mapActions('toast', {
			showToast: 'showToast',
		}),
		...mapActions('report', {
			getReportData: 'getReportData',
			getStockReportData: 'getStockReportData',
		}),
		...mapActions({
			getChannels: 'channelList/getChannels',
			getBrands: 'brandList/getBrands',
			getCategories: 'categoryList/getCategories',
		}),
		fetchData() {
			return Promise.all([
				this.getReportData(this.query),
				this.getStockReportData(),
				this.getChannels(),
			]);
		},
		onInputEnter(e) {
			this.query.page = null;
			this.query.keyword = e.target.value;
			this.updateUrl(this.query);
		},
		onResetClick() {
			this.query.keyword = null;
			this.query.brand = null;
			this.query.status = null;
			this.query.type = null;
			this.query.sort_by = null;
			this.query.sort_direction = null;
			this.query.stock_ageing = null;
			this.query.category_id = null;
			this.query.page = null;
			this.query.r = null;
			this.updateUrl(this.query);
		},
		handleBrandChange({ value }) {
			this.query.brand = value;
			this.query.page = null;
			this.updateUrl(this.query);
		},
		handleStatusChange({ value }) {
			this.query.status = value;
			this.query.page = null;
			this.updateUrl(this.query);
		},
		handleTypeChange({ value }) {
			this.query.type = value;
			this.query.page = null;
			this.updateUrl(this.query);
		},
		handleSortClick(key = '', state = false) {
			this.query.sort_by = key;
			this.query.sort_direction = state;
			this.updateUrl(this.query);
		},
		handlePageClick(pageNum) {
			this.query.page = pageNum;
			this.updateUrl(this.query);
		},
		handleCategory(id) {
			this.query.page = null;
			this.query.category_id = id;
			this.updateUrl(this.query);
		},
		async exportReport() {
			this.loadingExportBtn = true;
			const params = {
				'filters[brand_id]': this.query.brand,
				'filters[status]': this.query.status,
				'filters[product_type]': this.query.type,
				'filters[stock_ageing]': this.query.stock_ageing,
				'filters[category_id]': this.query.category_id,
				keyword: this.query.keyword,
			};
			try {
				await getExportReport(params);
				this.loadingExportBtn = false;
			} catch (error) {
				this.loadingExportBtn = false;
				this.showToast({
					header: this.$t('toast.error'),
					content: 'Unable to download the document. Please try again.',
					type: 'danger',
					timeout: 10000, // 10 secs
				});
			}
		},
		handleCheckedAdjustColumn(isChecked, channelId) {
			const add = (value) => {
				this.adjustColumn.push(value);
			};
			const remove = (value) => {
				const index = this.adjustColumn.findIndex((id) => id === value);
				const found = index >= 0;
				if (found) {
					this.adjustColumn.splice(index, 1);
				}
			};

			if (isChecked) {
				add(channelId);
			} else {
				remove(channelId);
			}

			localStorage.setItem('adjust_column', JSON.stringify(this.adjustColumn));
		},
		handleStockAging(value) {
			this.checkedStockAging = value;
			this.query.stock_ageing = value ? '1' : '0';
			this.query.page = null;
			this.updateUrl(this.query);
		},
	},
};
</script>

<style lang="scss" scoped>
	::v-deep .table-custom {
		margin-top: rem(32);

		.channel,
		.categories {
			&-cel {
				word-break: normal;
			}
		}

		.available-stock,
		.online-stock,
		.offline-stock,
		.reserved,
		.committed {
			// .available-stock-col, .online-stock-col, .offline-stock-col, .reserved-col, .committed-col
			&-col {
				min-width: rem(110 + $table-cell-padding);
			}
		}

		.over-stock {
			color: $color-black-45;
			font-size: rem($font-size-bullet-2);
			line-height: $line-height-h1;
			display: block;
		}
	}

	::v-deep .container-filter-header {
		min-width: rem(300);

		// .container-filter-header-action
		&-action {
			// .container-filter-header-action-right
			&-right {
				width: 40%;
			}

			&.container-adjust {
				&-column {
					padding: 0 rem(-12);
				}
			}
		}

		// .container-filter-header-action-custom
		&-action-custom {
			display: flex;

			.export-report-btn {
				background-color: $color-black;
				color: $color-white-95;
			}
		}
	}

	.container-adjust-column {
		width: rem(280);
	}

	.category-list {
		background-color: $color-alto;
		border-radius: rem(4);
		color: $color-black-45;
		padding: 0 rem(4);
	}

</style>
